import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import environment from '~/settings/environments';
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  saveAccessToken,
  saveRefreshToken,
} from '~/utils/storage';

import {
  isEmailNotVerifiedError,
  showEmailNotVerifiedModal,
} from './email-not-verified';

type FailedQueueItem = {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
};

let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error || !token) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
};

const api: AxiosInstance = axios.create({
  baseURL: environment.api.url,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    const isRefreshCall =
      originalRequest?.url?.includes('/v1/auth/refresh') ?? false;
    const isAuthEndpoint =
      originalRequest?.url?.includes('/v1/auth/') ?? false;
    const status = error.response?.status;

    if (isEmailNotVerifiedError(error)) {
      showEmailNotVerifiedModal(error, api);
      return Promise.reject(error);
    }

    if (status === 401 && isRefreshCall) {
      processQueue(error, null);
      isRefreshing = false;
      forceLogout();
      return Promise.reject(error);
    }

    if (status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        try {
          const token = await new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers = originalRequest.headers ?? {};
          (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      isRefreshing = true;
      try {
        const newToken = await refreshTokens();
        processQueue(null, newToken);
        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

async function refreshTokens(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    forceLogout();
    throw new Error('No refresh token');
  }
  const { data } = await api.post('/v1/auth/refresh', { refreshToken });
  if (!data?.tokens?.accessToken || !data?.tokens?.refreshToken) {
    throw new Error('Malformed refresh response');
  }
  saveAccessToken(data.tokens.accessToken);
  saveRefreshToken(data.tokens.refreshToken);
  return data.tokens.accessToken;
}

let onUnauthorized: (() => void) | null = null;

export function registerUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

function forceLogout() {
  removeTokens();
  if (onUnauthorized) onUnauthorized();
}

export default api;
